import { Grid, Select } from "@mantine/core";
import moment from "moment-jalaali";
import { FC, useEffect } from "react";

interface BirthDateSelectorProps {
  day?: string | null;
  month?: string | null;
  year?: string | null;
  onDayChange: (value: string | null) => void;
  onMonthChange: (value: string | null) => void;
  onYearChange: (value: string | null) => void;
  disabled?: boolean;
  dayError?: string;
  monthError?: string;
  yearError?: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

const BirthDateSelector: FC<BirthDateSelectorProps> = ({
  day,
  month,
  year,
  onDayChange,
  onMonthChange,
  onYearChange,
  disabled = false,
  dayError,
  monthError,
  yearError,
  size = "lg",
}) => {
  // Calculate the number of days based on Persian calendar rules
  const getDaysInMonth = (
    monthValue: string | null,
    yearValue: string | null
  ) => {
    if (!monthValue) return 31; // Default to 31 if no month selected

    const monthNum = parseInt(monthValue);

    // First 6 months (فروردین to شهریور): 31 days
    if (monthNum >= 1 && monthNum <= 6) {
      return 31;
    }

    // Next 5 months (مهر to بهمن): 30 days
    if (monthNum >= 7 && monthNum <= 11) {
      return 30;
    }

    // اسفند (month 12): 29 days normally, 30 in leap years
    if (monthNum === 12) {
      if (yearValue) {
        const jYear = parseInt(yearValue);
        // Check if it's a leap year in Jalali calendar
        const isLeapYear = moment.jIsLeapYear(jYear);
        return isLeapYear ? 30 : 29;
      }
      return 29; // Default to 29 if year not selected
    }

    return 31; // Fallback
  };

  const maxDays = getDaysInMonth(month ?? null, year ?? null);

  // Generate days based on the selected month and year
  const days = Array.from({ length: maxDays }, (_, i) => ({
    value: String(i + 1).padStart(2, "0"),
    label: String(i + 1),
  }));

  // Auto-adjust day if it's invalid for the selected month/year
  useEffect(() => {
    if (day && parseInt(day) > maxDays) {
      // Reset day to the last valid day of the month
      onDayChange(String(maxDays).padStart(2, "0"));
    }
  }, [month, year, maxDays, day, onDayChange]);

  // Generate months (1-12) with Persian names
  const months = [
    { value: "01", label: "فروردین" },
    { value: "02", label: "اردیبهشت" },
    { value: "03", label: "خرداد" },
    { value: "04", label: "تیر" },
    { value: "05", label: "مرداد" },
    { value: "06", label: "شهریور" },
    { value: "07", label: "مهر" },
    { value: "08", label: "آبان" },
    { value: "09", label: "آذر" },
    { value: "10", label: "دی" },
    { value: "11", label: "بهمن" },
    { value: "12", label: "اسفند" },
  ];

  // Generate years (1300 to current year for Persian calendar)
  const currentYear = parseInt(moment().format("jYYYY"));
  const startYear = 1300;
  const yearRange = currentYear - startYear + 1;
  const years = Array.from({ length: yearRange }, (_, i) => {
    const yearValue = currentYear - i;
    return {
      value: String(yearValue),
      label: String(yearValue),
    };
  });

  return (
    <>
      <Grid.Col span={4}>
        <Select
          size={size}
          placeholder="روز"
          label="روز تولد"
          data={days}
          value={day ?? undefined}
          onChange={onDayChange}
          disabled={disabled}
          error={dayError}
          searchable
          styles={{
            input: {
              textAlign: "center",
            },
            label: {
              fontSize: "1rem",
            },
          }}
          comboboxProps={{ shadow: "md" }}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <Select
          size={size}
          placeholder="ماه"
          label="ماه تولد"
          data={months}
          value={month ?? undefined}
          onChange={onMonthChange}
          disabled={disabled}
          error={monthError}
          searchable
          styles={{
            input: {
              textAlign: "center",
            },
            label: {
              fontSize: "1rem",
            },
          }}
          comboboxProps={{ shadow: "md" }}
        />
      </Grid.Col>

      <Grid.Col span={4}>
        <Select
          size={size}
          placeholder="سال"
          label="سال تولد"
          data={years}
          value={year ?? undefined}
          onChange={onYearChange}
          disabled={disabled}
          error={yearError}
          searchable
          styles={{
            input: {
              textAlign: "center",
            },
            label: {
              fontSize: "1rem",
            },
          }}
          comboboxProps={{ shadow: "md" }}
        />
      </Grid.Col>
    </>
  );
};

export default BirthDateSelector;
